import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Plane, Trash2, Edit, Calendar, Hotel, Paperclip, FileText, Eye, Unlink,
  AlertTriangle, TrendingUp, CheckCircle2,
} from 'lucide-react';
import { formatDate } from './tripCardHelpers';

// Renders a single stop row in the expanded trip card
const StopRow = ({ stop, locationLabel, locationStyle }) => {
  const isHotel = stop.type === 'hotel' || stop.type === 'both';
  const hotelText = (stop.hotel_name || 'Hotel') + (stop.location ? `, ${stop.location}` : '');
  const travelText = stop.description || '';
  const hasCheckout = isHotel && stop.checkout_date;

  return (
    <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-2.5 text-sm space-y-1">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {isHotel ? <Hotel className="h-3.5 w-3.5 text-amber-400" /> : <Plane className="h-3.5 w-3.5 text-purple-400" />}
        </div>
        <Badge variant="outline" className={`${locationStyle} text-[10px] px-1.5 py-0`}>{locationLabel}</Badge>
        <span className="text-slate-300">
          {isHotel ? hotelText : (travelText || 'Travel day')}
        </span>
        <span className="text-purple-300 font-medium ml-auto flex-shrink-0">
          {stop.date && formatDate(stop.date)}
          {hasCheckout && <span className="text-slate-500 mx-1">→</span>}
          {hasCheckout && formatDate(stop.checkout_date)}
        </span>
      </div>
      {isHotel && travelText && (
        <div className="flex items-center gap-2 pl-5">
          <Plane className="h-3 w-3 text-purple-400/60 flex-shrink-0" />
          <span className="text-slate-500 text-xs">{travelText}</span>
        </div>
      )}
    </div>
  );
};

export const TripCardExpanded = ({
  trip, flags, dateWarnings, isOngoing, linkedDocs = [],
  onEdit, onDelete, onMarkReturned, onAttachDoc, onPreviewDoc, onUnlinkDoc,
}) => {
  const f = flags;
  const isPartialTaxYear = trip.partial_tax_year?.is_partial;
  const preUkStops = trip.pre_uk_stops || [];
  const preAbroadStops = trip.pre_abroad_stops || [];
  const postAbroadStops = trip.post_abroad_stops || [];
  const postUkStops = trip.post_uk_stops || [];

  return (
    <div className="px-5 pb-5 border-t border-slate-700">
      {/* Alert Boxes */}
      <div className="pt-4 space-y-3">
        {isPartialTaxYear && (
          <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
            <p className="text-sm font-medium text-purple-400 mb-1">
              <Calendar className="h-4 w-4 inline mr-1" />
              Tax Year Boundary Trip
            </p>
            <p className="text-xs text-slate-400">
              This trip spans the April 6th tax year boundary.
              {trip.partial_tax_year.starts_before_tax_year && " It started in the previous tax year."}
              {trip.partial_tax_year.ends_after_tax_year && " It continues into the next tax year."}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div className="bg-slate-800/50 rounded p-2">
                <span className="text-slate-500">Full trip qualifying days:</span>
                <span className="text-purple-300 font-medium ml-1">{trip.partial_tax_year.full_trip_qualifying_days}</span>
              </div>
              <div className="bg-slate-800/50 rounded p-2">
                <span className="text-slate-500">This tax year:</span>
                <span className="text-amber-400 font-medium ml-1">{trip.partial_tax_year.tax_year_qualifying_days}</span>
              </div>
            </div>
          </div>
        )}

        {trip.overlap_info?.has_overlap && (
          <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
            <p className="text-sm font-medium text-amber-400 mb-1">Shared Day Note</p>
            <p className="text-xs text-slate-400">
              {trip.overlap_info.absorbed_date} is shared with <strong className="text-amber-300">{trip.overlap_info.absorbed_by_trip_name}</strong>.
              The day is counted for that trip, not this one.
            </p>
          </div>
        )}

        {f.isContinuous && (
          <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
            <p className="text-sm font-medium text-cyan-400 mb-1">Continuous Trip</p>
            <p className="text-xs text-slate-400">
              {trip.no_uk_departure && trip.no_uk_return
                ? "You were already abroad before this trip and stayed abroad after."
                : trip.no_uk_departure
                  ? "You were already abroad before this trip started."
                  : "You stayed abroad after this trip ended."}
              {" "}All days count as full days abroad.
            </p>
          </div>
        )}

        {/* Date Consistency Warnings */}
        {dateWarnings.hasWarnings && (
          <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30" data-testid="date-warnings">
            <p className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" />
              Date Consistency Warning
            </p>
            {dateWarnings.orderIssues.length > 0 && (
              <div className="mb-2">
                {dateWarnings.orderIssues.map((issue, idx) => (
                  <p key={`order-${idx}`} className="text-xs text-red-400 mb-1">{issue}</p>
                ))}
              </div>
            )}
            {dateWarnings.gaps.length > 0 && (
              <div className="mb-2">
                <p className="text-xs text-amber-300 mb-1">
                  {dateWarnings.gaps.length} earning day{dateWarnings.gaps.length !== 1 ? 's' : ''} with no rate or stop assigned:
                </p>
                <div className="flex flex-wrap gap-1">
                  {dateWarnings.gaps.slice(0, 10).map((g, idx) => (
                    <Badge key={`gap-${idx}`} variant="outline" className="border-amber-500/50 text-amber-400 text-[10px]">
                      {g.dateStr}
                    </Badge>
                  ))}
                  {dateWarnings.gaps.length > 10 && (
                    <Badge variant="outline" className="border-amber-500/50 text-amber-400 text-[10px]">
                      +{dateWarnings.gaps.length - 10} more
                    </Badge>
                  )}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">These days fall within your earning period but have no day rate or travel/hotel stop. Please review your dates.</p>
              </div>
            )}
            {dateWarnings.overlaps.length > 0 && (
              <div>
                <p className="text-xs text-blue-300 mb-1">
                  {dateWarnings.overlaps.length} day{dateWarnings.overlaps.length !== 1 ? 's' : ''} covered by multiple segments (counted once):
                </p>
                <div className="flex flex-wrap gap-1">
                  {dateWarnings.overlaps.slice(0, 10).map((o, idx) => (
                    <Badge key={`olap-${idx}`} variant="outline" className="border-blue-500/50 text-blue-400 text-[10px]">
                      {o.dateStr}
                    </Badge>
                  ))}
                  {dateWarnings.overlaps.length > 10 && (
                    <Badge variant="outline" className="border-blue-500/50 text-blue-400 text-[10px]">
                      +{dateWarnings.overlaps.length - 10} more
                    </Badge>
                  )}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Overlapping days (e.g. hotel checkout + vessel join) are only counted once.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pre-trip Travel/Hotel Stops */}
      {f.hasPreTravel && (
        <div className="mt-4">
          <p className="text-sm font-medium text-purple-400 mb-2">
            <Plane className="h-4 w-4 inline mr-1" />
            Before Joining Vessel
          </p>
          <div className="space-y-1.5">
            {preUkStops.length > 0 && preUkStops.map((stop, idx) => (
              <StopRow key={`pre-uk-${idx}`} stop={stop} locationLabel="UK" locationStyle="border-slate-600 text-slate-500" />
            ))}
            {trip.pre_departure_date && (
              <div className="flex items-center gap-2 bg-purple-500/5 border border-purple-500/20 rounded-lg p-2.5 text-sm">
                <Plane className="h-3.5 w-3.5 text-purple-400 flex-shrink-0" />
                <span className="text-slate-300">Left UK</span>
                <span className="text-purple-300 font-medium ml-auto">{formatDate(trip.pre_departure_date)}</span>
              </div>
            )}
            {preAbroadStops.length > 0 && preAbroadStops.map((stop, idx) => (
              <StopRow key={`pre-abroad-${idx}`} stop={stop} locationLabel="Abroad" locationStyle="border-cyan-600 text-cyan-500" />
            ))}
          </div>
        </div>
      )}

      {/* Post-trip Travel/Hotel Stops */}
      {f.hasPostTravel && (
        <div className="mt-4">
          <p className="text-sm font-medium text-purple-400 mb-2">
            <Plane className="h-4 w-4 inline mr-1" />
            After Leaving Vessel
          </p>
          <div className="space-y-1.5">
            {postAbroadStops.length > 0 && postAbroadStops.map((stop, idx) => (
              <StopRow key={`post-abroad-${idx}`} stop={stop} locationLabel="Abroad" locationStyle="border-cyan-600 text-cyan-500" />
            ))}
            {trip.post_return_date && (
              <div className="flex items-center gap-2 bg-purple-500/5 border border-purple-500/20 rounded-lg p-2.5 text-sm">
                <Plane className="h-3.5 w-3.5 text-purple-400 flex-shrink-0" />
                <span className="text-slate-300">Returned to UK</span>
                <span className="text-purple-300 font-medium ml-auto">{formatDate(trip.post_return_date)}</span>
              </div>
            )}
            {postUkStops.length > 0 && postUkStops.map((stop, idx) => (
              <StopRow key={`post-uk-${idx}`} stop={stop} locationLabel="UK" locationStyle="border-slate-600 text-slate-500" />
            ))}
          </div>
        </div>
      )}

      {/* Trip Details Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        {trip.date_joined_vessel && (
          <div className="bg-slate-700/30 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Joined Vessel</p>
            <p className="text-sm text-slate-300 font-medium mt-1">{formatDate(trip.date_joined_vessel)}</p>
          </div>
        )}
        {trip.date_left_vessel && (
          <div className="bg-slate-700/30 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Left Vessel</p>
            <p className="text-sm text-slate-300 font-medium mt-1">{formatDate(trip.date_left_vessel)}</p>
          </div>
        )}
        {trip.port_joining && (
          <div className="bg-slate-700/30 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Port of Joining</p>
            <p className="text-sm text-slate-300 font-medium mt-1">{trip.port_joining}</p>
          </div>
        )}
        {trip.port_leaving && (
          <div className="bg-slate-700/30 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Port of Leaving</p>
            <p className="text-sm text-slate-300 font-medium mt-1">{trip.port_leaving}</p>
          </div>
        )}
        {f.isCourse && trip.course_provider && (
          <div className="bg-slate-700/30 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Course Provider</p>
            <p className="text-sm text-slate-300 font-medium mt-1">{trip.course_provider}</p>
          </div>
        )}
        {f.isCourseForeign && trip.course_location && (
          <div className="bg-slate-700/30 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Location</p>
            <p className="text-sm text-slate-300 font-medium mt-1">{trip.course_location}</p>
          </div>
        )}
        {f.isUkHotelStay && trip.hotel_reason && (
          <div className="bg-slate-700/30 rounded-lg p-3 col-span-2">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Reason for Stay</p>
            <p className="text-sm text-slate-300 font-medium mt-1">{trip.hotel_reason}</p>
          </div>
        )}
      </div>

      {/* Uplifts Section */}
      {trip.uplifts && trip.uplifts.length > 0 && (
        <div className="mt-4" data-testid="uplifts-detail">
          <p className="text-sm font-medium text-amber-400 mb-2">
            <TrendingUp className="h-4 w-4 inline mr-1" />
            Uplifts ({trip.uplifts.length})
          </p>
          <div className="space-y-1.5">
            {trip.uplifts.map((uplift, idx) => (
              <div key={uplift.id || `uplift-${uplift.name}-${idx}`} className="flex items-center justify-between bg-amber-500/5 border border-amber-500/20 rounded-lg p-2.5 text-sm">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />
                  <span className="text-slate-300">{uplift.name || 'Uplift'}</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  {uplift.date_from && uplift.date_to ? (
                    <span className="text-slate-400">{formatDate(uplift.date_from)} — {formatDate(uplift.date_to)}</span>
                  ) : (
                    <span className="text-slate-500 italic">Whole trip</span>
                  )}
                  <span className="text-amber-400 font-medium">+£{parseFloat(uplift.amount || 0).toFixed(2)}/day</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* UK Water Crossings for multi-leg voyages */}
      {f.isMultiLeg && (
        <div className="mt-4">
          <p className="text-sm font-medium text-amber-400 mb-2">UK Water Crossings ({trip.uk_water_crossings.length + 1} legs)</p>
          <div className="space-y-2">
            {(trip.date_left_uk || trip.date_returned_uk) && (
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-amber-400 font-medium">Leg 1</span>
                  <span className="text-slate-400">
                    {trip.date_left_uk ? formatDate(trip.date_left_uk) : '—'} → {trip.date_returned_uk ? formatDate(trip.date_returned_uk) : '—'}
                  </span>
                </div>
              </div>
            )}
            {trip.uk_water_crossings.map((crossing, idx) => (
              <div key={crossing.date_left_uk_waters || `leg-${idx}`} className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-amber-400 font-medium">Leg {idx + 2}</span>
                  <span className="text-slate-400">
                    {formatDate(crossing.date_left_uk_waters)} → {formatDate(crossing.date_returned_uk_waters)}
                  </span>
                </div>
                {(crossing.port_left || crossing.port_returned) && (
                  <div className="text-xs text-slate-500 mt-1">
                    {crossing.port_left && <span>From: {crossing.port_left}</span>}
                    {crossing.port_left && crossing.port_returned && <span> • </span>}
                    {crossing.port_returned && <span>To: {crossing.port_returned}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attached Documents */}
      {(linkedDocs.length > 0 || onAttachDoc) && (
        <div className="mt-4" data-testid={`trip-docs-${trip.id}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-300 flex items-center gap-1.5">
              <Paperclip className="h-3.5 w-3.5 text-slate-400" />
              Documents ({linkedDocs.length})
            </p>
            {onAttachDoc && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onAttachDoc(trip); }}
                className="h-7 text-xs border-slate-600 text-slate-400 hover:text-amber-400 hover:border-amber-500/30"
                data-testid={`attach-doc-${trip.id}`}
              >
                <Paperclip className="h-3 w-3 mr-1" />
                Attach
              </Button>
            )}
          </div>
          {linkedDocs.length > 0 && (
            <div className="space-y-1.5">
              {linkedDocs.map(doc => (
                <div key={doc.id} className="flex items-center justify-between bg-slate-700/30 rounded-lg px-3 py-2 group">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                    <span className="text-sm text-slate-300 truncate">{doc.file_name}</span>
                    <span className="text-xs text-slate-500 shrink-0">{doc.document_type?.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {onPreviewDoc && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); onPreviewDoc(doc); }}
                        className="h-7 w-7 text-slate-500 hover:text-amber-400"
                        data-testid={`preview-doc-${doc.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {onUnlinkDoc && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); onUnlinkDoc(doc); }}
                        className="h-7 w-7 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`unlink-doc-${doc.id}`}
                      >
                        <Unlink className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Notes */}
      {trip.notes && (
        <div className="mt-4 bg-slate-700/30 rounded-lg p-3">
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Notes</p>
          <p className="text-sm text-slate-300">{trip.notes}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-slate-700">
        {isOngoing && onMarkReturned && (
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={(e) => { e.stopPropagation(); onMarkReturned(trip); }}
            className="border-sky-500/50 text-sky-400 hover:bg-sky-500/20"
            data-testid={`mark-returned-trip-${trip.id}`}
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Mark as returned
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          className="border-slate-600 text-slate-300 hover:bg-slate-700"
          data-testid={`edit-trip-${trip.id}`}
        >
          <Edit className="h-4 w-4 mr-2" />
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
          data-testid={`delete-trip-${trip.id}`}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </Button>
      </div>
    </div>
  );
};
